import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

/**
 * Photo-based Avatar Component
 * Uses user's uploaded photo and animates it during speech
 */
export default function PhotoAvatar({ 
  photoUrl, 
  isSpeaking, 
  audioLevel = 0,
  className = '' 
}) {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Load user's photo
  useEffect(() => {
    if (!photoUrl) return;

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      setImageLoaded(true);
    };
    img.src = photoUrl;
  }, [photoUrl]);

  // Animate canvas based on speech
  useEffect(() => {
    if (!imageLoaded || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const img = imageRef.current;

    let animationFrame;

    const animate = () => {
      // Clear canvas
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Calculate animation parameters
      const time = Date.now() / 1000;
      const scale = isSpeaking ? 1 + audioLevel * 0.15 : 1;
      const breathe = Math.sin(time * 1.5) * 0.02;
      const finalScale = scale + breathe;

      // Center point
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // Draw circular clipped image
      ctx.save();
      
      // Create circular clip path
      ctx.beginPath();
      ctx.arc(centerX, centerY, canvas.width / 2 - 10, 0, Math.PI * 2);
      ctx.closePath();
      ctx.clip();

      // Apply transformations
      ctx.translate(centerX, centerY);
      ctx.scale(finalScale, finalScale);
      ctx.translate(-centerX, -centerY);

      // Draw image
      const imgSize = canvas.width;
      ctx.drawImage(img, 0, 0, imgSize, imgSize);

      ctx.restore();

      // Outer glow when speaking
      if (isSpeaking && audioLevel > 0.1) {
        const glowIntensity = audioLevel * 255;
        const gradient = ctx.createRadialGradient(
          centerX, centerY, canvas.width / 2 - 20,
          centerX, centerY, canvas.width / 2 + 20
        );
        gradient.addColorStop(0, 'rgba(99, 102, 241, 0)');
        gradient.addColorStop(1, `rgba(99, 102, 241, ${audioLevel * 0.5})`);
        
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      // Audio waveform ring
      if (isSpeaking) {
        ctx.strokeStyle = `rgba(139, 92, 246, ${audioLevel})`;
        ctx.lineWidth = 4;
        ctx.beginPath();
        
        const numPoints = 60;
        for (let i = 0; i <= numPoints; i++) {
          const angle = (i / numPoints) * Math.PI * 2;
          const wave = Math.sin(time * 5 + i * 0.5) * audioLevel * 15;
          const radius = canvas.width / 2 + wave;
          const x = centerX + Math.cos(angle) * radius;
          const y = centerY + Math.sin(angle) * radius;
          
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        
        ctx.closePath();
        ctx.stroke();
      }

      animationFrame = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [imageLoaded, isSpeaking, audioLevel]);

  return (
    <div className={`relative ${className}`}>
      <canvas
        ref={canvasRef}
        width={400}
        height={400}
        className="w-full h-full"
      />
      
      {!photoUrl && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-900 to-blue-900 rounded-full">
          <div className="text-center text-white p-8">
            <svg className="w-20 h-20 mx-auto mb-4 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
            <p className="text-sm opacity-75">Fotoğraf yükleyin</p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Photo Upload Component
 */
export function PhotoUploader({ onPhotoUpload, currentPhoto }) {
  const fileInputRef = useRef(null);
  const [preview, setPreview] = useState(currentPhoto);
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Lütfen bir resim dosyası seçin');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Dosya boyutu en fazla 5MB olabilir');
      return;
    }

    setIsUploading(true);

    try {
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        const dataUrl = e.target.result;
        setPreview(dataUrl);
        onPhotoUpload(dataUrl, file);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Photo upload error:', error);
      alert('Fotoğraf yüklenemedi');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onPhotoUpload(null, null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        {preview ? (
          <div className="relative">
            <img
              src={preview}
              alt="Preview"
              className="w-24 h-24 rounded-full object-cover border-4 border-purple-500"
            />
            <button
              onClick={handleRemove}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
            >
              ×
            </button>
          </div>
        ) : (
          <div className="w-24 h-24 rounded-full bg-slate-700 flex items-center justify-center">
            <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
        )}

        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            className="hidden"
            id="photo-upload"
          />
          <label
            htmlFor="photo-upload"
            className={`inline-flex items-center px-4 py-2 rounded-lg font-medium cursor-pointer transition-colors ${
              isUploading
                ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                : 'bg-purple-600 text-white hover:bg-purple-700'
            }`}
          >
            {isUploading ? (
              <>
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Yükleniyor...
              </>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                {preview ? 'Değiştir' : 'Fotoğraf Yükle'}
              </>
            )}
          </label>
          <p className="text-xs text-slate-400 mt-1">
            JPG, PNG veya GIF (Max 5MB)
          </p>
        </div>
      </div>

      {preview && (
        <div className="text-xs text-slate-400">
          ✅ Fotoğraf yüklendi. Voice conversation'da bu fotoğrafınız görünecek.
        </div>
      )}
    </div>
  );
}
